import { useState } from 'react';
import { BarChart, ColumnChart, LineChart, shortNum } from '@ck/ui';
import { usd } from '@ck/sdk';
import { A, api } from '../api';
import { useMarket } from '../market';
import { Failed, Loading, useApi } from '../ui';

interface Series { scope: 'firm' | 'me'; days: string[]; signups: number[]; revenue: number[]; sales: number[]; payouts: number[] | null; byMarket: { brand: string; revenue: number; sales: number }[]; byCountry: { country: string; signups: number }[] }
const money = (v: number) => usd(v, 0);
const sum = (a: number[]) => a.reduce((x, y) => x + y, 0);

/** Revenue, sales, sign-ups and payouts as line, trend and bar charts. "me" shows the signed-in person's own book. */
export function StatsCharts({ scope }: { scope: 'firm' | 'me' }) {
  const [days, setDays] = useState(30);
  const { market, markets } = useMarket();
  const d = useApi(() => api.get<Series>(A(`/stats/series?days=${days}&scope=${scope}${market !== 'all' ? `&brand=${market}` : ''}`)), [days, scope, market]);
  const mk = (b: string) => markets.find((m) => m.brand === b);
  return (
    <section className="charts">
      <div className="filters" style={{ alignItems: 'center' }}>
        <h2 className="sec" style={{ margin: 0, marginRight: 'auto' }}>{d.data?.scope === 'me' ? 'My numbers' : 'Firm numbers'}</h2>
        <div className="seg" role="group">{[7, 30, 90, 365].map((n) => <button key={n} className={days === n ? 'on' : ''} onClick={() => setDays(n)}>{n === 365 ? '1 year' : `${n} days`}</button>)}</div>
      </div>
      {d.error ? <Failed error={d.error} retry={d.reload} /> : !d.data ? <Loading /> : (
        <div className="chart-grid">
          <div className="panel"><div className="panel-h"><h3>Revenue</h3><b>{money(sum(d.data.revenue))}</b></div>
            <LineChart label="Revenue per day" days={d.data.days} series={[{ name: 'Revenue', values: d.data.revenue, color: '#FEAB41', format: money }, ...(d.data.payouts ? [{ name: 'Payouts paid', values: d.data.payouts, color: '#E5484D', format: money }] : [])]} /></div>
          <div className="panel"><div className="panel-h"><h3>Challenges sold</h3><b>{sum(d.data.sales)}</b></div>
            <ColumnChart label="Challenges sold per day" days={d.data.days} values={d.data.sales} color="#17A08A" /></div>
          <div className="panel"><div className="panel-h"><h3>{d.data.scope === 'me' ? 'New clients in my book' : 'New sign-ups'}</h3><b>{sum(d.data.signups)}</b></div>
            <LineChart label="Sign-ups per day" days={d.data.days} series={[{ name: 'Sign-ups', values: d.data.signups, color: '#3563E9' }]} /></div>
          <div className="panel"><div className="panel-h"><h3>By market and country</h3></div>
            <BarChart label="Revenue by market" format={money} rows={d.data.byMarket.map((r) => ({ label: mk(r.brand)?.name ?? r.brand, value: r.revenue, color: mk(r.brand)?.accent }))} />
            <h4 className="sub-h">Sign-ups by country</h4>
            <BarChart label="Sign-ups by country" format={shortNum} color="#7A5CE0" rows={d.data.byCountry.map((r) => ({ label: r.country, value: r.signups }))} />
          </div>
        </div>
      )}
    </section>
  );
}
